import { faNewspaper } from "@fortawesome/free-regular-svg-icons"
import { faLocationDot, faMedal, faPhone, faUser, faX } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useEffect, useRef, useState } from "react"
import Accordion from "../Accordion"
import data from '../../../data.json'
import GetWindowSize from '../../../hooks/general/GetWindowSize'
import axios from "axios"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import ProfilePhotoUploader from "../../../hooks/images/ProfilePhotoUploader"
import LocationSuggestions from "../../../hooks/general/LocationSuggestions"
import MultiMediaUploader from "../../../hooks/images/MultiMediaUploader"
import LoadingAnimation from "../LoadingAnimation"

interface FormStructure {
    fullname: string,
    phonenumber: string,
    location: string,
    biography: string,
    goals: string[],
    primarySport: string,
    sportInterests: string[],
    experienceLevel: string
}

interface FormProps {
    displayForm: (value:boolean) => void;
    prevSavedData?: ProfileDetails;
    savedMediaFiles: string[];
    setSavedMediaFiles: React.Dispatch<React.SetStateAction<string[]>>;
}

interface Review {
    id: number;
    title: string;
    description: string;
    rating: string;
    date: string;
}

interface Service {
    type: string;
    title: string;
    description: string;
    duration: string;
    frequency?: string;
    target_audience?: string;
    location?: string;
    deliverable?: string;
    price: number;
}
  
interface ProfileDetails {
    fullname: string;
    email: string;
    iscoach: boolean;
    isathlete: boolean;
    phonenumber: string;
    profile: {
        location: string;
        biography: string;
        primary_sport: string;
        picture: string | null;
        reviews: Review[];
        rating: number;
        instagram?: string,
        facebook?: string,
        twitter?: string,
        tiktok?: string
    };
    preferences: {
        experience_level: string;
        goals?: string[];
        sport_interests?: string[];
        age_groups?: string[];
        specialization?: string[];
        services?: Service[];
    }
}

export default function EditAthleteProfileForm({displayForm, prevSavedData, savedMediaFiles, setSavedMediaFiles}:FormProps){
    const [formData, setFormData] = useState<FormStructure>({
        fullname: prevSavedData?.fullname || "",
        phonenumber: prevSavedData?.phonenumber || "",
        location: prevSavedData?.profile.location || "",
        biography: prevSavedData?.profile.biography || "",
        goals: prevSavedData?.preferences.goals || [],
        primarySport: prevSavedData?.profile.primary_sport || "",
        sportInterests: prevSavedData?.preferences.sport_interests || [],
        experienceLevel: prevSavedData?.preferences.experience_level || ""
    })
    const [currentPrimarySport, setCurrentPrimarySport] = useState<string>(prevSavedData?.profile.primary_sport || "")
    const [insideForm, setInsideForm] = useState<boolean>(false)
    const windowSize = GetWindowSize()
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
    const [focusedInputId, setFocusedInputId] = useState<string | null>(null)
    const [uploadComplete, setUploadComplete] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [readyToUpload, setReadyToUpload] = useState<boolean>(false)

    // Updates which element is currently in focus
    const handleFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocusedInputId(event.target.id);
    }

    // reload page once media upload and form updates have been saved
    useEffect(() => {
        if (uploadComplete) {
            window.location.reload()
            // delay updating submitting so UI inside form matches API success speed
            const timer = setTimeout(() => {
                setSubmitting(false)
            }, 10000);
            return clearTimeout(timer)
        }
    }, [uploadComplete])
    
    // Handles form submission/saving
    function handleSubmit(e:React.FormEvent){
        e.preventDefault()
        setSubmitting(true)
        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/auth/profile/update_athlete_profile/`, formData, {
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
            })
            .then(res => {
                // Reload page to update profile information with new changes
                if (res.status === 201) {
                    setReadyToUpload(true)
                } else {
                    console.error("submission failed")
                    setSubmitting(false)
                }
            })
            .catch(error => {console.error('Error:', error)})
    }

    // Close the form if user clicks outside of it
    function handleExit(value:boolean){
        if(!insideForm){
            displayForm(value)
        }
    }

    // Handle input changes for text, text area, and button fields
    function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value, dataset } = e.target

        if (name.toLowerCase().includes('goal') && dataset.index !== undefined) {
            const index = Number(dataset.index)
            let updatedGoals = [...formData.goals]
            if (!formData.goals[index]) {
                updatedGoals.push(value)
            } else {
                updatedGoals[index] = value
            }
            setFormData(prevState => ({ ...prevState, goals: updatedGoals }))
        
        } else if (name === 'sportInterests' && dataset.index !== undefined) {
            const index = Number(dataset.index)
            const updatedSports = [...formData.sportInterests]
            updatedSports[index] = value
            setFormData(prevState => ({ ...prevState, sportInterests: updatedSports }))
        } else {
            setFormData(prevState => ({ ...prevState, [name]: value }))
        }
    }

    // Update form state when radio buttons are selected
    function handleRadioChange(e: React.ChangeEvent<HTMLInputElement>) {
        setFormData({ ...formData, experienceLevel: e.target.value })
    }

    // Toggle sports of interest when accordion options are clicked
    function handleAccordionChange(e:React.MouseEvent<HTMLButtonElement>){
        e.preventDefault()
        const duplicateAnswer = formData.sportInterests.includes(e.currentTarget.value)
        const filteredInput = formData.sportInterests.filter(currInput=>currInput!==e.currentTarget.value)
        if(!duplicateAnswer){
            setFormData({ ...formData, sportInterests: [...formData.sportInterests,e.currentTarget.value]})
        }
        else{
            setFormData({ ...formData, sportInterests: filteredInput})
        }
    }

    // Define icons used across the form
    const icons: Record<string, any> = {
        faUser,
        faPhone,
        faLocationDot,
        faNewspaper,
        faMedal
    }

    // Render form inputs dynamically based on their type
    function renderInput(input: any) {
        const IconComponent = icons[input.icon]
        if (input.type === 'radio') {
            return (
                <div 
                    key={input.id} 
                    className={`flex items-center form-input border-main-grey-100 px-0 my-2 w-full ${formData.experienceLevel === input.value? 'bg-main-green-500':''}`}
                >
                    <input 
                        type="radio" 
                        id={input.id}
                        name={input.name}
                        value={input.value}
                        checked={formData.experienceLevel === input.value}
                        onChange={handleRadioChange}
                        className="form-radio-input w-min"
                    />
                    <label htmlFor={input.id} className={`w-full text-main-green-900 font-semibold cursor-pointer text-center ${formData.experienceLevel === input.value? 'text-main-white':''}`}>
                        {input.label}
                    </label>
                </div>
            )
        }
        return ( 
            <div key={input.id} className="relative w-full mb-5 mt-5"> 
                {input.type === 'textarea' ? (
                    <textarea
                        id={input.id}
                        name={input.name}
                        className="form-input w-full border-main-grey-100 max-h-24 min-h-24"
                        placeholder={input.placeholder}
                        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                        value={formData[input.name as keyof FormStructure] as string}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        maxLength={220}
                    />
                ) : ( input.name.includes('Goal') ? 
                        <input
                            id={input.id}
                            type={input.type}
                            name={input.name}
                            className="form-input w-full border-main-grey-100"
                            placeholder={formData.goals[input.index] as string || input.placeholder}
                            value={formData[input.name as keyof FormStructure] as string}
                            ref={inputRef as React.RefObject<HTMLInputElement>}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            maxLength={110}
                            data-index={input.index || ''}
                        />
                    :
                    (
                        input.id === 'location' ?
                            <div className="relative">
                                <input
                                    id={input.id}
                                    type={input.type}
                                    name={input.name}
                                    className="form-input w-full border-main-grey-100"
                                    placeholder={input.placeholder}
                                    value={formData[input.name as keyof FormStructure] as string}
                                    onChange={handleChange}
                                    ref={inputRef as React.RefObject<HTMLInputElement>}
                                    onFocus={handleFocus}
                                    maxLength={110}
                                    data-index={input.index || ''}
                                    autoComplete="off"
                                />
                                {
                                focusedInputId === 'location' && (
                                    <LocationSuggestions
                                        <FormStructure>
                                        locationQuery={formData.location}
                                        inView={true}
                                        insideForm={true}
                                        updateFormLocation={setFormData}
                                    />
                                )}

                            </div>
                        :
                            <input
                                id={input.id}
                                type={input.type}
                                name={input.name}
                                className="form-input w-full border-main-grey-100"
                                placeholder={input.placeholder}
                                ref={inputRef as React.RefObject<HTMLInputElement>}
                                value={formData[input.name as keyof FormStructure] as string}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                maxLength={110}
                                data-index={input.index || ''}
                            />
                    )
                )}
                <FontAwesomeIcon
                    icon={IconComponent}
                    className="absolute inset-y-4 left-0 flex items-center pl-4 text-main-grey-500"
                />
            </div>
        )
    }

    return (
        <div className="pop-up-background" 
            onClick={windowSize.width >=1024 ? 
                ()=>handleExit(false) 
            : 
                undefined
            }
        > 
            <div 
                className="pop-up-container h-4/5"
                onMouseEnter={()=>setInsideForm(true)} 
                onMouseLeave={()=>setInsideForm(false)}
            >
                <div className="grid grid-cols-3 grid-rows-1 p-5 px-7 lg:px-10 bg-main-white rounded-t-xl">
                    <FontAwesomeIcon 
                        icon={faX}
                        className="text-main-green-900 hover:text-main-green-500 mr-auto my-auto cursor-pointer"
                        onClick={()=>handleExit(false)}
                        onMouseEnter={()=>setInsideForm(false)}
                    />
                    <h3 className="text-lg font-semibold font-kulim underline mx-auto text-center">
                        Edit Profile
                    </h3>
                    <p 
                        className="text-main-green-900 font-kulim my-auto hover:text-main-green-500 ml-auto cursor-pointer"
                        onClick={handleSubmit}
                    >
                        Save
                    </p>
                </div>
                {submitting && 
                    <div className="mt-36 mx-auto"> 
                        <LoadingAnimation />
                    </div>
                }
                {/* styling is used to hide form as removing it from DOM while animation load prevents child from receiving updated states */}
                <form className={`${submitting ? 'opacity-0':'opacity-100'} flex flex-col bg-main-white p-4 lg:px-10 rounded-b-xl`} onSubmit={handleSubmit}>
                    {/* <ProfilePhotoUploader token={csrfToken!}/> */}
                    <MultiMediaUploader UploadCompletion={setUploadComplete} token={csrfToken!} updateReady={readyToUpload} storedMediaFiles={savedMediaFiles} setStoredMediaFiles={setSavedMediaFiles}/>
                    <div className="relative">
                        <p>Personal Information</p>
                        {data.profileForms.athlete.personalInformation.map(renderInput)}

                        <p className="text-xs text-main-grey-200 text-center mb-8 px-6">
                            {data.profileForms.athlete.warningInfo}
                        </p>
                        <p className="my-6">
                            My Current Goals
                        </p>
                        {data.profileForms.athlete.goals.map(renderInput)}

                        <p className="my-6">
                            Primary Sport
                        </p>
                        {formData.sportInterests.length !== 0 || prevSavedData?.profile.primary_sport ?
                            <>
                            {formData.sportInterests.map((currSport,index)=>(
                                <button 
                                    onClick={(e)=>{
                                        e.preventDefault(); 
                                        setFormData({...formData, primarySport: currSport});
                                        setCurrentPrimarySport(currSport);
                                    }}
                                    key={index}
                                    className={`py-2 px-4 rounded-xl mr-2 border border-main-grey-100 cursor-pointer
                                        ${currentPrimarySport === currSport ? "bg-main-green-500 text-white" : "bg-white text-black"}
                                        hover:bg-main-green-600 hover:text-white`}
                                >
                                    {currSport}
                                </button>
                            ))}
                            </>

                        :
                            <p className="text-sm text-main-grey-200 text-center my-4">
                                No Primary Sport Available
                            </p>
                        }

                        <p className="my-6">
                            Sports of Interest
                        </p>
                        <Accordion title="Individual Sports" styles="border-main-grey-100 border rounded-xl">
                            {data.profileForms.athlete.sportOptions.individual.map((option, index) => (
                                <button 
                                    onClick={(e)=>{e.preventDefault(); handleAccordionChange(e)}}
                                    key={`individual-${index}`} 
                                    value={option}
                                    aria-label={`team sports option for ${option}`}
                                    className={`text-left p-3 hover:bg-main-green-700 hover:text-main-color-white ${formData.sportInterests.includes(option)? 'bg-main-green-500 text-main-white':''}`}
                                >
                                    {option}
                                </button>
                            ))}
                        </Accordion>
                        <Accordion title="Team Sports" styles="border-main-grey-100 border rounded-xl mt-2">
                            {data.profileForms.athlete.sportOptions.team.map((option, index) => (
                                <button 
                                    onClick={(e)=>{e.preventDefault(); handleAccordionChange(e)}}
                                    key={`individual-${index}`} 
                                    value={option}
                                    aria-label={`team sports option for ${option}`}
                                    className={`text-left p-3 hover:bg-main-green-700 hover:text-main-color-white ${formData.sportInterests.includes(option)? 'bg-main-green-500 text-main-white':''}`}
                                >
                                    {option}
                                </button>
                            ))}
                        </Accordion>

                        <p className="my-6">Experience Level</p>
                        {data.profileForms.athlete.experienceLevel.map(renderInput)}
                    </div>
                </form>
            </div>
        </div>
    )
}